export const TELEMETRY_CONSENT_VERSION = 2 as const;
export const CRASH_DIAGNOSTICS_NOTICE_VERSION = '2026-08-14' as const;

export type OptionalPurposeState = 'uninitialized' | 'denied' | 'granted';
export type TelemetryRuntime =
  | 'web'
  | 'electron-renderer'
  | 'electron-main'
  | 'electron-preload'
  | 'worker';

export interface TelemetryConsent {
  version: typeof TELEMETRY_CONSENT_VERSION;
  productAnalytics: OptionalPurposeState;
  crashDiagnostics: OptionalPurposeState;
  crashDiagnosticsDecision?: {
    decidedAt: string;
    noticeVersion: typeof CRASH_DIAGNOSTICS_NOTICE_VERSION;
    appVersion: string;
  };
  sessionReplay: 'unavailable';
}

export const DEFAULT_TELEMETRY_CONSENT: TelemetryConsent = Object.freeze({
  version: TELEMETRY_CONSENT_VERSION,
  productAnalytics: 'uninitialized',
  crashDiagnostics: 'uninitialized',
  sessionReplay: 'unavailable',
});

const purposeStates = new Set<OptionalPurposeState>([
  'uninitialized',
  'denied',
  'granted',
]);

function normalizedCrashDiagnosticsDecision(value: unknown) {
  if (typeof value !== 'object' || value === null) return undefined;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.decidedAt !== 'string' ||
    Number.isNaN(Date.parse(candidate.decidedAt)) ||
    candidate.noticeVersion !== CRASH_DIAGNOSTICS_NOTICE_VERSION ||
    typeof candidate.appVersion !== 'string' ||
    candidate.appVersion.length === 0
  ) {
    return undefined;
  }
  return {
    decidedAt: candidate.decidedAt,
    noticeVersion: CRASH_DIAGNOSTICS_NOTICE_VERSION,
    appVersion: candidate.appVersion,
  };
}

/** Legacy true/missing never proves consent; explicit false stays denial. */
export function normalizeTelemetryConsent(value: unknown): TelemetryConsent {
  if (typeof value !== 'object' || value === null) {
    return DEFAULT_TELEMETRY_CONSENT;
  }
  const candidate = value as Record<string, unknown>;
  const nested = candidate.telemetryConsent;
  if (typeof nested === 'object' && nested !== null) {
    const consent = nested as Record<string, unknown>;
    const storedCrashDiagnostics = purposeStates.has(
      consent.crashDiagnostics as OptionalPurposeState
    )
      ? (consent.crashDiagnostics as OptionalPurposeState)
      : 'uninitialized';
    const crashDiagnosticsDecision = normalizedCrashDiagnosticsDecision(
      consent.crashDiagnosticsDecision
    );
    const crashDiagnostics =
      storedCrashDiagnostics === 'granted' && !crashDiagnosticsDecision
        ? 'uninitialized'
        : storedCrashDiagnostics;
    return {
      version: TELEMETRY_CONSENT_VERSION,
      productAnalytics: purposeStates.has(
        consent.productAnalytics as OptionalPurposeState
      )
        ? (consent.productAnalytics as OptionalPurposeState)
        : 'uninitialized',
      crashDiagnostics,
      ...(crashDiagnosticsDecision ? { crashDiagnosticsDecision } : {}),
      sessionReplay: 'unavailable',
    };
  }
  if (candidate.enableTelemetry === false) {
    return {
      ...DEFAULT_TELEMETRY_CONSENT,
      productAnalytics: 'denied',
      crashDiagnostics: 'denied',
    };
  }
  return DEFAULT_TELEMETRY_CONSENT;
}

export function isPurposeAllowed(
  consent: TelemetryConsent,
  purpose: 'productAnalytics' | 'crashDiagnostics' | 'sessionReplay'
): boolean {
  return purpose !== 'sessionReplay' && consent[purpose] === 'granted';
}
